import React, { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

const MAX_MB = 8;

export function ImageUpload({ value, onChange, label = "Photo", hint = "JPG, PNG or WebP · min 480×360 · up to 8 MB", shape = "wide", testId = "image-upload", required }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const pick = () => inputRef.current?.click();

  const upload = async (file) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG or WebP image"); return; }
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(`Image must be under ${MAX_MB} MB`); return; }
    setBusy(true); setProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      onChange(data.url);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const dims = shape === "square" ? "w-40 h-40 rounded-3xl" : "w-full h-52 sm:h-64 rounded-2xl";

  return (
    <div data-testid={testId}>
      <p className="text-sm font-bold text-ink">{label}{required && <span className="text-coral"> *</span>}</p>
      <div className={`mt-1.5 relative ${dims} overflow-hidden border-2 border-dashed ${value ? "border-border" : "border-coral/40 bg-coral/5"} group`}>
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <button type="button" onClick={pick} data-testid={`${testId}-pick`} className="absolute inset-0 grid place-items-center text-center px-4">
            <span className="flex flex-col items-center gap-2 text-ink-soft">
              <span className="grid place-items-center w-12 h-12 rounded-full bg-white shadow-soft text-coral"><Camera size={22} /></span>
              <span className="text-sm font-bold text-ink">Tap to upload</span>
              <span className="text-xs">{hint}</span>
            </span>
          </button>
        )}
        {busy && (
          <div className="absolute inset-0 grid place-items-center bg-white/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-2 text-ink">
              <Loader2 className="animate-spin text-coral" size={26} />
              <span className="text-sm font-bold">{progress < 100 ? `Uploading ${progress}%` : "Optimizing…"}</span>
            </div>
          </div>
        )}
        {value && !busy && (
          <div className="absolute inset-x-0 bottom-0 flex justify-end gap-2 p-2 bg-gradient-to-t from-ink/60 to-transparent">
            <button type="button" onClick={pick} data-testid={`${testId}-replace`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white text-ink text-xs font-bold shadow-soft"><Camera size={14} /> Replace</button>
            <button type="button" onClick={() => onChange("")} data-testid={`${testId}-remove`} aria-label="Remove photo" className="grid place-items-center w-9 h-9 rounded-full bg-white text-ink shadow-soft"><X size={14} /></button>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid={`${testId}-input`}
        onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}
